// One product's cost roll-up (WP5.2): line by line, each component with its cost and where
// that cost comes from, opening sub-recipes (intermediates) to show how they add up in turn,
// with the coverage over everything nested ("computed for X of Y; missing: …").

import type { ProductCost } from '@enp/domain/api';
import { formatNumber } from '@enp/domain/format';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  cn,
  EmptyState,
  focusRing,
  Money,
  PageHeader,
  Qty,
  Skeleton,
  StatusBadge,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { ArrowLeft, BookOpen, ChevronRight, PackageSearch, RefreshCw } from 'lucide-react';
import { Fragment, useState } from 'react';
import { ApiError } from '../../../../lib/api.js';
import {
  cell,
  Fact,
  Facts,
  Note,
  numCell,
  Panel,
  PlainTable,
  useCan,
} from '../../../../production/parts.js';
import {
  invalidateProduction,
  productCostQuery,
  runCostRollup,
} from '../../../../production/queries.js';
import {
  COST_SOURCE_LABEL,
  coverageSentence,
  coverageStatus,
  onlyItselfMissing,
  linesCostedText,
  rollupBatchCents,
  refusalText,
  sentence,
  unitCostText,
  yieldText,
} from '../../../../production/words.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { actionProblem } from '../../../../stock-control/parts.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/recipes/costs/$productId')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.productId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(productCostQuery(params.productId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: CostPage,
  pendingComponent: CostSkeleton,
  errorComponent: CostError,
  notFoundComponent: CostNotFound,
});

type Line = ProductCost['lines'][number];

/** Where a component's cost comes from, in a few words. */
function SourceText({ cost }: { cost: ProductCost | undefined }) {
  if (!cost) return <span className="text-muted">…</span>;
  const source = cost.stored.cost_source ?? cost.computed.source;
  if (!source) return <span className="text-muted">None</span>;
  return <span>{COST_SOURCE_LABEL[source]}</span>;
}

function LineRows({
  line,
  depth,
  path,
}: {
  line: Line;
  depth: number;
  /** Products above this row (a loop is never opened). */
  path: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const child = useQuery(productCostQuery(line.component.id));
  const sub = child.data;
  const expandable =
    sub !== undefined &&
    sub.recipe_version !== null &&
    sub.lines.length > 0 &&
    !path.includes(line.component.id);
  const pad = ['pl-3', 'pl-9', 'pl-14', 'pl-20', 'pl-24'][Math.min(depth, 4)];
  return (
    <>
      <tr data-depth={depth}>
        <th scope="row" className={cn(cell, pad, 'text-left font-normal')}>
          <span className="flex items-start gap-1.5">
            {expandable ? (
              <button
                type="button"
                aria-expanded={open}
                aria-label={`${open ? 'Hide' : 'Show'} how ${line.component.name} is made`}
                onClick={() => setOpen((o) => !o)}
                className={cn(
                  '-ml-1 mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-muted hover:bg-surface-hover hover:text-fg',
                  focusRing,
                )}
              >
                <ChevronRight
                  aria-hidden
                  className={cn('size-4 transition-transform', open && 'rotate-90')}
                />
              </button>
            ) : (
              <span aria-hidden className="inline-block size-5 shrink-0" />
            )}
            <span className="flex flex-col leading-tight">
              <span className="font-medium text-fg">{line.component.name}</span>
              {sub?.recipe_version && (
                <span className="text-xs text-muted">Made here, from its own recipe</span>
              )}
            </span>
          </span>
        </th>
        <td className={numCell}>
          <Qty value={line.qty} unit={line.uom} maxDecimals={6} />
          {Number(line.loss_pct) > 0 && (
            <span className="block text-xs text-muted">
              + {formatNumber(line.loss_pct, { maxDecimals: 6 })}% loss
            </span>
          )}
        </td>
        <td className={numCell}>
          {line.unit_cost_cents === null ? (
            <span className="text-muted">None</span>
          ) : (
            unitCostText(line.unit_cost_cents, line.component.base_uom)
          )}
        </td>
        <td className={numCell}>
          {line.line_cost_cents === null ? (
            <span className="text-muted">—</span>
          ) : (
            <Money cents={line.line_cost_cents} className="font-medium text-fg" />
          )}
        </td>
        <td className={cn(cell, 'text-sm')}>
          {line.gap ? (
            <span className="text-state-danger">{sentence(refusalText(line.gap.message))}</span>
          ) : (
            <SourceText cost={sub} />
          )}
        </td>
      </tr>
      {open && sub && (
        <>
          <tr>
            <td
              colSpan={5}
              className={cn(
                cell,
                ['pl-9', 'pl-14', 'pl-20', 'pl-24', 'pl-28'][Math.min(depth, 4)],
                'bg-surface-sunken py-1.5 text-xs text-muted',
              )}
            >
              One batch of {line.component.name}:{' '}
              {sub.batch_yield_qty && sub.yield_uom
                ? yieldText(sub.batch_yield_qty, sub.yield_uom).toLowerCase()
                : 'its recipe'}
              {sub.computed.cents !== null &&
                `, ${unitCostText(sub.computed.cents, line.component.base_uom)}`}
            </td>
          </tr>
          {sub.lines.map((l) => (
            <LineRows
              key={l.component.id}
              line={l}
              depth={depth + 1}
              path={[...path, line.component.id]}
            />
          ))}
        </>
      )}
    </>
  );
}

function CostPage() {
  const { productId } = Route.useParams();
  const { data: cost } = useSuspenseQuery(productCostQuery(productId));
  useDocumentTitle(`Cost of ${cost.product.name}`);
  const queryClient = useQueryClient();
  const canRun = useCan('catalogue.edit', true);
  const base = cost.product.base_uom;
  const batch = rollupBatchCents(cost);
  const coverage = coverageStatus(cost.coverage, cost.product.name);
  // Its own gap is said once, in the note below with the way to fix it (UI5A-203).
  const ownGapOnly = !!cost.computed.gap && onlyItselfMissing(cost.coverage, cost.product.name);
  const stored = cost.stored.standard_cost_cents;
  // The first line that has no cost: where to go to fix it (UI5A-11).
  const gapLine = cost.lines.find((l) => l.gap !== null);

  const rollup = useMutation({
    mutationFn: () => runCostRollup([productId]),
    onSuccess: async (r) => {
      await invalidateProduction(queryClient);
      toast.success('Cost rolled up', {
        description:
          r.changes.length > 0
            ? `Changed: ${r.changes.map((c) => c.product.name).join(', ')}. ${coverageSentence(r.coverage)}`
            : `Nothing changed. ${coverageSentence(r.coverage)}`,
      });
    },
    onError: (error) => {
      const p = actionProblem(error, 'The cost wasn’t rolled up');
      toast.error(p.title, { description: p.explain });
    },
  });

  return (
    <>
      <PageHeader
        eyebrow={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/recipes">Recipes</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/recipes/costs">Costs</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{cost.product.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
        title={`Cost of ${cost.product.name}`}
        description="How the standard cost adds up: each ingredient’s cost, the quantity one batch uses, and the loss."
        actions={
          <>
            <Button variant="secondary" asChild>
              <Link to="/catalogue/$productId" params={{ productId }}>
                <BookOpen aria-hidden />
                Product in the catalogue
              </Link>
            </Button>
            {canRun && (
              <Button
                icon={<RefreshCw aria-hidden />}
                loading={rollup.isPending}
                onClick={() => rollup.mutate()}
              >
                Roll up this cost
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-6">
        <Panel id="summary" title="Standard cost">
          <Facts className="lg:grid-cols-4">
            <Fact label="Stored (reports and approvals use it)">
              {stored === null ? (
                <span className="text-muted">None yet</span>
              ) : (
                <span className="font-display text-xl font-semibold">
                  {unitCostText(stored, base)}
                </span>
              )}
            </Fact>
            <Fact label="Where it comes from">
              {cost.stored.cost_source ? COST_SOURCE_LABEL[cost.stored.cost_source] : '—'}
              {cost.stored.cost_updated_at && (
                <span className="block text-sm text-muted">
                  Updated <AccountDate value={cost.stored.cost_updated_at} withTime />
                </span>
              )}
            </Fact>
            <Fact label="One batch">
              {batch !== null ? (
                <>
                  <Money cents={batch} />
                  {cost.batch_yield_qty && cost.yield_uom && (
                    <span className="block text-sm text-muted">
                      {yieldText(cost.batch_yield_qty, cost.yield_uom)}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted">
                  {cost.lines.length === 0 ? '—' : 'Not worked out: a line has no cost'}
                </span>
              )}
            </Fact>
            <Fact label="Recipe rolled up">
              {cost.recipe_version ? (
                cost.recipe_version.status === 'active' ? (
                  `Version ${cost.recipe_version.version_no}, in use`
                ) : (
                  <>
                    Draft version {cost.recipe_version.version_no}
                    <span className="block text-sm text-muted">
                      Not signed off: nothing is in use yet, so the cost comes from the draft and
                      may change.
                    </span>
                  </>
                )
              ) : (
                <span className="text-muted">No recipe: bought in</span>
              )}
            </Fact>
          </Facts>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={coverage.status}>{coverage.label}</StatusBadge>
            {!ownGapOnly && (
              <p className="text-body text-fg" data-testid="coverage-summary">
                {coverageSentence(cost.coverage, cost.product.name)}
              </p>
            )}
          </div>
          {cost.computed.gap && (
            <Note>
              <span className="flex flex-col gap-1">
                <span data-testid="cost-gap">
                  {/* "3 of 4 lines costed: …" — one sentence (UI5A-203). */}
                  {linesCostedText(cost.lines)?.replace(/\.$/, ':') ??
                    'The cost can’t be worked out from the recipe:'}{' '}
                  {refusalText(cost.computed.gap.message)}.
                </span>
                {gapLine && (
                  <Link
                    to="/catalogue/$productId"
                    params={{ productId: gapLine.component.id }}
                    className="font-medium text-primary-text underline underline-offset-4"
                  >
                    {gapLine.gap?.code === 'missing_conversion'
                      ? `Add a unit conversion to ${gapLine.component.name}`
                      : `Open ${gapLine.component.name} in the catalogue`}
                  </Link>
                )}
              </span>
            </Note>
          )}
          {cost.computed.cents !== null && stored !== null && cost.computed.cents !== stored && (
            <Note tone="neutral">
              Rolled up now it would be {unitCostText(cost.computed.cents, base)}.{' '}
              {cost.stored.cost_source === 'admin' || cost.stored.cost_source === 'manual'
                ? 'A person set the stored cost, so a roll-up keeps it.'
                : 'Roll it up to store the new cost.'}
            </Note>
          )}
        </Panel>

        <Panel
          id="lines"
          title="How one batch adds up"
          description="Open a sub-recipe to see how its own cost adds up."
        >
          {cost.lines.length === 0 ? (
            <p className="text-body text-muted">
              {cost.recipe_version
                ? 'The recipe has no ingredients.'
                : 'Bought in, so there is nothing to roll up: its cost is its purchase cost.'}
            </p>
          ) : (
            <PlainTable
              label="Cost lines"
              className="-mx-5"
              head={[
                'Ingredient',
                'One batch uses',
                'Its cost',
                'Line cost',
                'Where the cost comes from',
              ]}
              align={['left', 'right', 'right', 'right', 'left']}
              foot={
                <tr className="border-t border-border-strong">
                  <th scope="row" className="px-3 py-2 pl-8 text-left font-semibold text-fg">
                    One batch
                  </th>
                  <td />
                  <td />
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold">
                    {batch === null ? (
                      <span className="text-muted">Incomplete</span>
                    ) : (
                      <Money cents={batch} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-muted">
                    {cost.computed.cents !== null && unitCostText(cost.computed.cents, base)}
                  </td>
                </tr>
              }
            >
              {cost.lines.map((l) => (
                <Fragment key={l.component.id}>
                  <LineRows line={l} depth={0} path={[productId]} />
                </Fragment>
              ))}
            </PlainTable>
          )}
        </Panel>
      </div>
    </>
  );
}

function CostSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading the cost</h1>
      <span className="sr-only">Loading the cost…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-8 w-80" />
      </div>
      <div className="flex flex-col gap-6">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-80 rounded-lg" />
      </div>
    </div>
  );
}

function BackButton() {
  return (
    <Button variant="secondary" asChild>
      <Link to="/recipes/costs">
        <ArrowLeft aria-hidden />
        Back to costs
      </Link>
    </Button>
  );
}

function CostError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="max-w-2xl">
      <ErrorPanel
        error={error}
        what="This cost"
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        extraAction={<BackButton />}
      />
    </div>
  );
}

function CostNotFound() {
  useDocumentTitle('Product not found');
  return (
    <div className="max-w-2xl">
      <EmptyState
        icon={PackageSearch}
        titleAs="h1"
        title="Product not found"
        description="There’s no product at this address. It may be in another account, or the link may be mistyped."
        action={<BackButton />}
      />
    </div>
  );
}
